import React, { useMemo } from 'react';
import Tooltip from '../components/Tooltip';
import { ActionsFeature, TblCol } from './table.types';

type TblBodyProps = {
    actions?: ActionsFeature[];
    cols: TblCol[];
    rows: any[];
    onRowSelect?: (rows: any[]) => void;
    onClick?: (rowItem: any) => void;
    selectedRows?: Record<string, any>;
};

const TableBody = ({ actions, cols, rows, onClick, selectedRows }: TblBodyProps): JSX.Element => {
    /**
     * Fix the indexes in this because this is probably the bug...
     */
    return (
        <tbody className="tbl-body">
            {rows.map((row, idx) => (
                <TblRow
                    key={`row-${row?.Id}-${idx}`}
                    selected={selectedRows?.[row?.Id] ?? false}
                    cols={cols}
                    row={row}
                    index={idx}
                    actions={actions}
                    onClick={onClick}
                />
            ))}
        </tbody>
    );
};

type TblRowProps = {
    cols: TblCol[];
    selected?: boolean;
    row: any;
    index: number;
    actions?: ActionsFeature[];
    onClick?: (rowItem: any) => void;
};

const _TblRow = ({ cols, selected, row, index, actions, onClick }: TblRowProps): JSX.Element => {
    const className = useMemo(() => {
        let name = 'table-list-item';

        if (!selected) {
            name += index % 2 === 0 ? ' row-shade' : '';
        } else {
            name += ' selected';
        }
        return name;
    }, [index, selected]);

    return (
        <tr className={className}>
            {actions?.map((action, key) => {
                const Column = action.cellRenderer;
                return <td key={`${action.columnTitle}-${key}`}>{<Column row={row} />}</td>;
            })}
            {cols.map(col => (
                <TblBodyCell
                    key={`${col.key}-${row[col.key]}`}
                    onClick={onClick}
                    colKey={col.key}
                    colRenderer={col?.renderer}
                    row={row}
                    tooltip={col?.tooltip}
                />
            ))}
        </tr>
    );
};

const TblRow = React.memo(_TblRow);

type TblBodyCellProps = Pick<TblBodyProps, 'onClick'> & {
    colKey: string;
    colRenderer?: any;
    row: any;
    tooltip?: string | ((row: any) => string);
};

const TblBodyCell = ({ onClick, colKey, colRenderer, row, tooltip }: TblBodyCellProps): JSX.Element => {
    let ColRenderer: any = null;
    if (colRenderer) {
        ColRenderer = colRenderer;
    }

    let tooltipContent: string | undefined = undefined;
    if (typeof tooltip === 'function') {
        tooltipContent = tooltip(row);
    } else if (typeof tooltip === 'string') {
        tooltipContent = tooltip;
    }

    const cellContent = colRenderer ? (
        <ColRenderer
            key={colKey}
            item={row}
            colKey={colKey}
        />
    ) : (
        row[colKey]
    );

    return (
        <td
            key={colKey}
            {...(onClick ? { onClick: (): void => onClick(row) } : {})}
        >
            {tooltipContent ? <Tooltip content={tooltipContent}>{cellContent}</Tooltip> : cellContent}
        </td>
    );
};

export default React.memo(TableBody);
